import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { SyncStats } from './calendarApi';

// --- Types (mirror backend modules/calendar/calendarConnections.service) ----

export interface CalendarConnection {
  id: string;
  userId: string;
  provider: string;
  calendarEmail: string;
  calendarLabel: string | null;
  status: 'connected' | 'expired';
  isActive: boolean;
  needsReauth: boolean;
  lastError: string | null;
  connectedAt: string;
  lastSyncAt: string | null;
}

export interface CalendarConnectionsResponse {
  connections: CalendarConnection[];
  googleConfigured: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// GET /api/users/:userId/calendars
export async function listUserCalendars(userId: string): Promise<CalendarConnectionsResponse> {
  const res = await apiFetch<Envelope<CalendarConnectionsResponse>>(
    `/api/users/${userId}/calendars`,
    { headers: authHeaders() },
  );
  return res.data;
}

// POST /api/users/:userId/calendars/connect -> { authUrl }
export async function startCalendarConnect(
  userId: string,
  label?: string,
): Promise<{ authUrl: string }> {
  const res = await apiFetch<Envelope<{ authUrl: string }>>(
    `/api/users/${userId}/calendars/connect`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(label ? { label } : {}),
    },
  );
  return res.data;
}

// DELETE /api/users/:userId/calendars/:calendarId
export async function disconnectCalendar(
  userId: string,
  calendarId: string,
): Promise<{ disconnected: boolean }> {
  const res = await apiFetch<Envelope<{ disconnected: boolean }>>(
    `/api/users/${userId}/calendars/${calendarId}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

// POST /api/users/:userId/calendars/:calendarId/sync
export async function syncCalendar(userId: string, calendarId: string): Promise<SyncStats> {
  const res = await apiFetch<Envelope<SyncStats>>(
    `/api/users/${userId}/calendars/${calendarId}/sync`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}
